import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Skeleton, ErrorState } from '@/components/ui'
import { AppWindow, Users, Activity, TrendingUp, ArrowRight, Plus } from 'lucide-react'
import { api, type AppSummaryInfo, type RecentActivity } from '@/api/client'

interface DashboardData {
  apps: AppSummaryInfo[]
  stats: {
    apps: number
    total_users: number
    api_calls_30d: number
    revenue_mtd_cents: number
  }
  activities: RecentActivity[]
}

export function OrgDashboardPage() {
  const { orgSlug } = useParams()
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!orgSlug) return

      try {
        setError(null)

        // Fetch apps list - this is the core data we need
        const appsResponse = await api.apps.list(orgSlug)

        // Calculate stats from apps
        const apps = appsResponse.apps
        const stats = {
          apps: apps.length,
          total_users: 0, // Would need aggregation endpoint
          api_calls_30d: 0, // Would need analytics endpoint
          revenue_mtd_cents: 0, // Would need billing endpoint
        }

        // Try to fetch activity, but don't fail if not available
        let activities: RecentActivity[] = []
        try {
          const activityResponse = await api.orgs.getActivity(orgSlug)
          activities = activityResponse.activities
        } catch {
          // Activity endpoint may not exist yet
          activities = []
        }

        setData({ apps, stats, activities })
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [orgSlug])

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Dashboard"
          description={`Overview for ${orgSlug}`}
          action={
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              New App
            </Button>
          }
        />
        {/* Stats skeleton */}
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Skeleton className="h-4 w-16 mb-2" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                  <Skeleton className="h-10 w-10 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Content skeleton */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-16" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div>
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-28" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-2 w-2 rounded-full mt-1.5" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-full mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Dashboard"
          description={`Overview for ${orgSlug}`}
        />
        <ErrorState
          title="Failed to load dashboard"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  if (!data) return null

  const stats = [
    { title: 'Apps', value: data.stats.apps, icon: AppWindow },
    { title: 'Total Users', value: data.stats.total_users.toLocaleString(), icon: Users },
    { title: 'API Calls (30d)', value: data.stats.api_calls_30d.toLocaleString(), icon: Activity },
    { title: 'Revenue (MTD)', value: `$${(data.stats.revenue_mtd_cents / 100).toFixed(2)}`, icon: TrendingUp },
  ]

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={`Overview for ${orgSlug}`}
        action={
          <Button asChild>
            <Link to={`/orgs/${orgSlug}/apps/new`}>
              <Plus className="mr-2 h-4 w-4" />
              New App
            </Link>
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4 mb-8">
        {stats.map((stat) => (
          <StatCard key={stat.title} title={stat.title} value={stat.value} icon={stat.icon} />
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Apps */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Apps</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/orgs/${orgSlug}/apps`} className="flex items-center gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {data.apps.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">No apps yet. Create your first app to get started.</p>
            ) : (
              <div className="space-y-4">
                {data.apps.slice(0, 3).map((app) => (
                  <Link
                    key={app.id}
                    to={`/orgs/${orgSlug}/apps/${app.slug}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-[var(--bg-elevated)] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)]">
                        {app.logo_url ? (
                          <img src={app.logo_url} alt={app.name} className="h-6 w-6 object-contain" />
                        ) : (
                          <AppWindow className="h-5 w-5 text-[var(--text-secondary)]" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {app.name}
                        </p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {app.slug}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={app.status === 'active' ? 'success' : 'default'}
                    >
                      {app.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {data.activities.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">No recent activity.</p>
            ) : (
              <div className="space-y-4">
                {data.activities.slice(0, 4).map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-[var(--accent)]" />
                    <div className="flex-1">
                      <p className="text-sm text-[var(--text-primary)]">{item.description}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {new Date(item.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ElementType
}

function StatCard({ title, value, icon: Icon }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[var(--text-secondary)]">{title}</p>
            <p className="text-3xl font-semibold text-[var(--text-primary)] mt-1">
              {value}
            </p>
          </div>
          <div className="rounded-full bg-[var(--bg-elevated-2)] p-3">
            <Icon className="h-5 w-5 text-[var(--text-secondary)]" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
